import { defineMessages } from 'react-intl';

const messages = defineMessages({
  Image: {
    id: 'Image',
    defaultMessage: 'Image',
  },
  AltText: {
    id: 'Alt text',
    defaultMessage: 'Alt text',
  },
  Align: {
    id: 'Alignment',
    defaultMessage: 'Alignment',
  },
  size: {
    id: 'Image size',
    defaultMessage: 'Image size',
  },
  LinkTo: {
    id: 'Link to',
    defaultMessage: 'Link to',
  },
  openLinkInNewTab: {
    id: 'Open in a new tab',
    defaultMessage: 'Open in a new tab',
  },
  AltTextHint: {
    id: 'Alt text hint',
    defaultMessage:
      'Obrigatório: descreva o conteúdo da imagem para leitores de tela.',
  },
  AltTextHintLinkText: {
    id: 'Alt text hint link text',
    defaultMessage: 'Como escrever um bom texto alternativo.',
  },
  linkSettings: {
    id: 'Link settings',
    defaultMessage: 'Link settings',
  },
  caption: {
    id: 'mpf_image_block_caption',
    defaultMessage: 'Legenda',
  },
  captionHint: {
    id: 'mpf_image_block_caption_hint',
    defaultMessage:
      'Texto exibido abaixo da imagem. Deixe em branco para não exibir legenda.',
  },
  ratio: {
    id: 'mpf_image_block_ratio',
    defaultMessage: 'Proporção',
  },
  ratioHint: {
    id: 'mpf_image_block_ratio_hint',
    defaultMessage:
      'Trava a altura na proporção escolhida, recortando a imagem pelo centro. Em branco, a imagem mantém a proporção do arquivo.',
  },
  ratioFree: {
    id: 'mpf_image_block_ratio_free',
    defaultMessage: 'Do arquivo',
  },
  ratio165: {
    id: 'mpf_image_block_ratio_16_5',
    defaultMessage: '16:5 — faixa de banner',
  },
  ratio163: {
    id: 'mpf_image_block_ratio_16_3',
    defaultMessage: '16:3 — faixa fina',
  },
  ratio169: {
    id: 'mpf_image_block_ratio_16_9',
    defaultMessage: '16:9 — widescreen',
  },
  ratio32: {
    id: 'mpf_image_block_ratio_3_2',
    defaultMessage: '3:2 — foto',
  },
  ratio916: {
    id: 'mpf_image_block_ratio_9_16',
    defaultMessage: '9:16 — vertical (Reels/Shorts)',
  },
});

export function ImageSchema({ formData, intl }) {
  return {
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        // `align`/`size` restaurados (removidos sem querer no commit que
        // adicionou `caption`) — RNF02 já limita as opções de `align` a
        // center/full, então devolver o campo ao editor não reabre o risco
        // de float/wrap.
        fields: [
          ...(formData.url ? ['alt', 'caption', 'ratio', 'align', 'size'] : []),
        ],
      },
      ...(formData.url
        ? [
            {
              id: 'link_settings',
              title: intl.formatMessage(messages.linkSettings),
              fields: ['href', 'openLinkInNewTab'],
            },
          ]
        : []),
    ],
    properties: {
      alt: {
        title: intl.formatMessage(messages.AltText),
        description: (
          <>
            <a
              href="https://www.w3.org/WAI/tutorials/images/decision-tree/"
              title={intl.formatMessage(messages.openLinkInNewTab)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage(messages.AltTextHintLinkText)}
            </a>{' '}
            {intl.formatMessage(messages.AltTextHint)}
          </>
        ),
      },
      caption: {
        title: intl.formatMessage(messages.caption),
        description: intl.formatMessage(messages.captionHint),
        widget: 'textarea',
      },
      // O banner do topo da sub-home costuma ser montado com este bloco, e o
      // Figma pede uma faixa de 16:5 — sem trava, a altura vem do arquivo e
      // o topo da página fica com o dobro da altura do design.
      //
      // SEM `default` de propósito: o `applyBlockDefaults` do Volto preenche
      // defaults de schema em tempo de render, para TODAS as imagens do
      // portal — um default aqui recortaria conteúdo já publicado.
      ratio: {
        title: intl.formatMessage(messages.ratio),
        description: intl.formatMessage(messages.ratioHint),
        choices: [
          ['', intl.formatMessage(messages.ratioFree)],
          ['16:5', intl.formatMessage(messages.ratio165)],
          ['16:3', intl.formatMessage(messages.ratio163)],
          ['16:9', intl.formatMessage(messages.ratio169)],
          ['3:2', intl.formatMessage(messages.ratio32)],
          ['9:16', intl.formatMessage(messages.ratio916)],
        ],
      },
      align: {
        title: intl.formatMessage(messages.Align),
        widget: 'align',
        // RNF02: parágrafo/imagem sempre em diagramação linear — sem
        // float/wrap no corpo de texto. `left`/`right` (as duas opções
        // removidas) são as únicas que aplicam `float` em `blocks.less` do
        // Volto core.
        actions: ['center', 'full'],
        default: 'center',
      },
      size: {
        title: intl.formatMessage(messages.size),
        widget: 'image_size',
        default: 'l',
      },
      href: {
        title: intl.formatMessage(messages.LinkTo),
        widget: 'object_browser',
        mode: 'link',
        selectedItemAttrs: ['Title', 'Description', 'hasPreviewImage'],
        allowExternals: true,
      },
      openLinkInNewTab: {
        title: intl.formatMessage(messages.openLinkInNewTab),
        type: 'boolean',
      },
    },
    // ALT obrigatório sempre que há imagem — acessibilidade (sem exceção de
    // decorativa). Sem imagem escolhida o campo nem aparece, então não exige.
    required: formData.url ? ['alt'] : [],
  };
}

export const gridImageDisableSizeAndPositionHandlersSchema = ({
  schema,
  formData,
  intl,
}) => {
  schema.fieldsets[0].fields = schema.fieldsets[0].fields.filter(
    (item) => !['align', 'size'].includes(item),
  );
  return schema;
};
